import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  MenuItem, 
  Grid, 
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TimelineIcon from '@mui/icons-material/Timeline';
import FilterListIcon from '@mui/icons-material/FilterList';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InfoIcon from '@mui/icons-material/Info';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MonteCarloAnalysis = () => {
    const [config, setConfig] = useState({
        iterations: 10000,
        distribution: 'triangular',
        department: '',
        category: ''
    });
    const [metadata, setMetadata] = useState({ departments: [], categories: [] });
    const [mapping, setMapping] = useState({
        1: 500,
        2: 5000,
        3: 20000,
        4: 100000,
        5: 500000
    });
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showSources, setShowSources] = useState(false);
    const resultsRef = React.useRef(null);

    React.useEffect(() => {
        const fetchMetadata = async () => {
            try {
                const res = await api.get('/risk-metadata');
                setMetadata(res.data);
            } catch (e) {
                console.error('Failed to fetch metadata');
            }
        };
        fetchMetadata();
    }, []);

    const runSimulation = async () => {
        setLoading(true);
        try {
            const response = await api.post('/monte-carlo/simulate', { ...config, mapping });
            setResults(response.data);
            setTimeout(() => {
                resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        } catch (error) {
            console.error('Simulation Failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const chartData = {
        labels: results?.chartData?.map(d => `$${(d.bin / 1000).toFixed(0)}k`) || [],
        datasets: [{
            label: 'Frequency',
            data: results?.chartData?.map(d => d.count) || [],
            backgroundColor: 'rgba(59, 130, 246, 0.6)',
            borderColor: '#3b82f6',
            borderWidth: 1,
            borderRadius: 4
        }]
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (context) => `Frequency: ${context.parsed.y} iterations`
                }
            }
        },
        scales: {
            x: { 
                grid: { display: false },
                ticks: { color: '#94a3b8', font: { size: 10 } }
            },
            y: { 
                grid: { color: 'rgba(255,255,255,0.05)' },
                ticks: { color: '#94a3b8', font: { size: 10 } }
            }
        }
    };

    const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Monte Carlo Simulation
                </Typography>
            </Box>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <Paper className="glass" sx={{ p: 3, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <Typography sx={{ color: 'white', fontWeight: 'bold', mb: 0.5 }}>Simulation Parameters</Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 3 }}>
                            Only 'Open' or 'In Progress' records are included in the model.
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Distribution Model"
                                    value={config.distribution}
                                    onChange={(e) => setConfig({ ...config, distribution: e.target.value })}
                                    sx={{ '& .MuiOutlinedInput-root': { color: 'white', background: 'rgba(255,255,255,0.03)' } }}
                                    InputLabelProps={{ sx: { color: '#94a3b8' } }}
                                >
                                    <MenuItem value="triangular">Triangular (Standard)</MenuItem>
                                    <MenuItem value="normal">Normal (Bell Curve)</MenuItem>
                                    <MenuItem value="uniform">Uniform (Equal Weight)</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Iterations"
                                    value={config.iterations}
                                    onChange={(e) => setConfig({ ...config, iterations: parseInt(e.target.value) || 0 })}
                                    sx={{ '& .MuiOutlinedInput-root': { color: 'white', background: 'rgba(255,255,255,0.03)' } }}
                                    InputLabelProps={{ sx: { color: '#94a3b8' } }}
                                />
                            </Grid>
                        </Grid>
                        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                            <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FilterListIcon sx={{ fontSize: '1rem' }} /> FILTER DATA SCOPE
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Department"
                                        value={config.department}
                                        onChange={(e) => setConfig({ ...config, department: e.target.value })}
                                        sx={{ '& .MuiOutlinedInput-root': { color: 'white', background: 'rgba(255,255,255,0.02)' } }}
                                        InputLabelProps={{ shrink: true, sx: { color: '#64748b' } }}
                                        SelectProps={{
                                            displayEmpty: true,
                                            renderValue: (v) => v || 'All Departments',
                                            MenuProps: { PaperProps: { sx: { bgcolor: '#1e293b', color: 'white' } } }
                                        }}
                                    >
                                        <MenuItem value="">All Departments</MenuItem>
                                        {(metadata.departments || []).map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Category"
                                        value={config.category}
                                        onChange={(e) => setConfig({ ...config, category: e.target.value })}
                                        sx={{ '& .MuiOutlinedInput-root': { color: 'white', background: 'rgba(255,255,255,0.02)' } }}
                                        InputLabelProps={{ shrink: true, sx: { color: '#64748b' } }}
                                        SelectProps={{
                                            displayEmpty: true,
                                            renderValue: (v) => v || 'All Categories',
                                            MenuProps: { PaperProps: { sx: { bgcolor: '#1e293b', color: 'white' } } }
                                        }}
                                    >
                                        <MenuItem value="">All Categories</MenuItem>
                                        {(metadata.categories || []).map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                    </TextField>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper className="glass" sx={{ p: 3, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <Typography sx={{ color: 'white', fontWeight: 'bold', mb: 3 }}>Financial Scale Configuration ($)</Typography>
                        <Grid container spacing={1}>
                            {[1, 2, 3, 4, 5].map(level => (
                                <Grid item xs={2.4} key={level}>
                                    <TextField
                                        size="small"
                                        label={`Lvl ${level}`}
                                        value={mapping[level]}
                                        onChange={(e) => setMapping({ ...mapping, [level]: parseInt(e.target.value) || 0 })}
                                        sx={{ 
                                            '& .MuiOutlinedInput-root': { color: 'white', background: 'rgba(255,255,255,0.02)', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { color: '#10b981', fontWeight: 'bold !important', fontSize: '0.8rem' }
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                        <Typography variant="caption" sx={{ color: '#64748b', mt: 1, display: 'block' }}>
                            Map qualitative consequence (1-5) to financial loss potential.
                        </Typography>
                    </Paper>
                    <Button 
                        variant="contained" 
                        fullWidth
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                        onClick={runSimulation}
                        disabled={loading}
                        sx={{ 
                            mt: 2,
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            py: 1.5,
                            borderRadius: '16px',
                            boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.2)'
                        }}
                    >
                        {loading ? 'Simulating...' : 'Run Quantitative Analysis'}
                    </Button>
                </Grid>
                
                {results && (
                    <Grid item xs={12} md={6}>
                        <Paper className="glass" sx={{ p: 3, background: 'rgba(16, 185, 129, 0.05)', borderRadius: '24px', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
                            <Typography sx={{ color: '#10b981', fontWeight: 'bold', mb: 2 }}>Execution Metrics</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: '0.85rem' }}>
                                <span>SLA Status: <strong style={{ color: '#10b981' }}>PASSED</strong></span>
                                <span>Execution Time: <strong>{results.metrics.execution_time}s</strong></span>
                                <span>Sample Size: <strong>{results.metrics.iterations.toLocaleString()}</strong></span>
                            </Box>
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {results && (
                <div ref={resultsRef}>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {[
                            { label: 'Mean Expected Loss', value: results.metrics.mean, icon: <TimelineIcon />, color: '#3b82f6' },
                            { label: 'Value at Risk (VaR 95%)', value: results.metrics.var95, icon: <TrendingDownIcon />, color: '#ef4444' },
                            { label: 'P90 (Worst Case 10%)', value: results.metrics.p90, icon: <BarChartIcon />, color: '#f59e0b' },
                            { label: 'P95 (Tail Risk)', value: results.metrics.p95, icon: <TrendingDownIcon />, color: '#dc2626' }
                        ].map((metric, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Card className="glass" sx={{ background: 'rgba(30, 41, 59, 0.4)', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Box sx={{ p: 1, borderRadius: '8px', background: `${metric.color}15`, color: metric.color }}>{metric.icon}</Box>
                                            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: '600' }}>{metric.label}</Typography>
                                        </Box>
                                        <Typography sx={{ color: 'white', fontSize: '1.4rem', fontWeight: 'bold' }}>
                                            {formatCurrency(metric.value)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Paper className="glass" sx={{ p: 4, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)', mb: 4 }}>
                        <Typography sx={{ color: 'white', fontWeight: 'bold', mb: 4 }}>Loss Distribution Curve (Histogram)</Typography>
                        <Box sx={{ height: 400 }}>
                            <Bar data={chartData} options={chartOptions} />
                        </Box>
                    </Paper>

                    <Paper className="glass" sx={{ p: 3, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <Typography sx={{ color: 'white', fontWeight: 'bold', mb: 3 }}>Scenario Exposure Breakdown</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ '& th': { color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.05)' } }}>
                                    <TableCell>Confidence Level</TableCell>
                                    <TableCell>Max Probable Loss</TableCell>
                                    <TableCell>Risk Impact Grade</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody sx={{ '& td': { color: 'white', borderBottom: '1px solid rgba(255,255,255,0.02)', py: 1.5 } }}>
                                <TableRow>
                                    <TableCell>P50 (Median)</TableCell>
                                    <TableCell>{formatCurrency(results.metrics.mean * 0.9)}</TableCell>
                                    <TableCell><Chip label="MODERATE" size="small" sx={{ bgcolor: 'rgba(234, 179, 8, 0.1)', color: '#eab308' }} /></TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>P90 (High Confidence)</TableCell>
                                    <TableCell>{formatCurrency(results.metrics.p90)}</TableCell>
                                    <TableCell><Chip label="HIGH EXPOSURE" size="small" sx={{ bgcolor: 'rgba(249, 115, 22, 0.1)', color: '#f97316' }} /></TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>P95 (Extreme Event)</TableCell>
                                    <TableCell>{formatCurrency(results.metrics.p95)}</TableCell>
                                    <TableCell><Chip label="EXTREME" size="small" sx={{ bgcolor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }} /></TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Paper>

                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                        <Button 
                            variant="outlined" 
                            startIcon={<ListAltIcon />}
                            onClick={() => setShowSources(!showSources)}
                            sx={{ 
                                color: '#94a3b8', 
                                borderColor: 'rgba(255,255,255,0.1)',
                                textTransform: 'none',
                                borderRadius: '12px',
                                '&:hover': { background: 'rgba(255,255,255,0.05)', borderColor: '#94a3b8' }
                            }}
                        >
                            {showSources ? 'Hide Source Data' : 'View Specific Records Used in Simulation'}
                        </Button>
                    </Box>

                    {showSources && (
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} md={4}>
                                <Paper className="glass" sx={{ p: 3, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    <Typography sx={{ color: 'white', fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ShieldIcon sx={{ color: '#10b981', fontSize: '1.2rem' }} /> Included Risk Records ({results.metrics.risk_count})
                                    </Typography>
                                    <Box sx={{ maxHeight: '300px', overflowY: 'auto', pr: 1 }}>
                                        {results.sources.risks.map((r, i) => (
                                            <Box key={i} sx={{ py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                <Typography sx={{ color: '#10b981', fontSize: '0.8rem', fontWeight: 'bold' }}>{r.id}</Typography>
                                                <Typography sx={{ color: '#f1f5f9', fontSize: '0.75rem' }}>{r.description}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper className="glass" sx={{ p: 3, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    <Typography sx={{ color: 'white', fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircleIcon sx={{ color: '#3b82f6', fontSize: '1.2rem' }} /> Included SHE Activities ({results.metrics.she_count})
                                    </Typography>
                                    <Box sx={{ maxHeight: '300px', overflowY: 'auto', pr: 1 }}>
                                        {results.sources.she.map((s, i) => (
                                            <Box key={i} sx={{ py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                <Typography sx={{ color: '#3b82f6', fontSize: '0.8rem', fontWeight: 'bold' }}>{s.id}</Typography>
                                                <Typography sx={{ color: '#f1f5f9', fontSize: '0.75rem' }}>{s.description}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper className="glass" sx={{ p: 3, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    <Typography sx={{ color: 'white', fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AssessmentIcon sx={{ color: '#f59e0b', fontSize: '1.2rem' }} /> Included Loss Events ({results.metrics.loss_count || 0})
                                    </Typography>
                                    <Box sx={{ maxHeight: '300px', overflowY: 'auto', pr: 1 }}>
                                        {(results.sources.losses || []).map((loss, i) => (
                                            <Box key={i} sx={{ py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                <Typography sx={{ color: '#f59e0b', fontSize: '0.8rem', fontWeight: 'bold' }}>{loss.id}</Typography>
                                                <Typography sx={{ color: '#f1f5f9', fontSize: '0.75rem' }}>{loss.description}</Typography>
                                            </Box>
                                        ))}
                                        {(!results.sources.losses || results.sources.losses.length === 0) && (
                                            <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>No loss events included.</Typography>
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </div>
            )}

            {!results && !loading && (
                <Box sx={{ textAlign: 'center', py: 10, color: '#475569' }}>
                    <TrendingDownIcon sx={{ fontSize: 60, mb: 2, opacity: 0.2 }} />
                </Box>
            )}
        </Box>
    );
};

export default MonteCarloAnalysis;
